/** @format */

import {
	BVEventObject,
	BVEventObjectName,
	BVEventObjectType,
	BVEventType,
	BVProduct,
	DropdownSelect,
	SelectedFilter
} from '../models';
import { BVPixelResponse, selectBVPixel } from '@product-reviews/data-access';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	inject,
	input
} from '@angular/core';

import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import {
	homefieldIconChevronDown,
	homefieldIconChevronUp,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { CommonModule } from '@angular/common';
import { HmfSelectorComponent } from '@dsg-tech/homefield-angular';
import { InsideViewportDirective } from '../directive/inside-viewport/inside-viewport.directive';
import { ClickDetectionHandlerDirective } from '../directive/click-detection-handler/click-detection-handler.directive';

@Component({
	selector: 'product-reviews-dropdown-select',
	templateUrl: './review-dropdown-select.component.html',
	styleUrl: './review-dropdown-select.component.scss',
	imports: [
		ClickDetectionHandlerDirective,
		CommonModule,
		HmfSelectorComponent,
		HomefieldIconLibraryModule,
		InsideViewportDirective
	]
})
export class ReviewDropdownSelectComponent implements OnInit, OnChanges {
	homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);
	store = inject(Store<BVPixelResponse>);

	@Input() ariaLabel = '';
	@Input() circleVariant = 'medium';
	@Input() disabled = false;
	@Input() dropdownList: DropdownSelect[] = [];
	@Input() filterName = '';
	@Input() isMobile = false;
	@Input() isMultipleChoice = false;
	@Input() label = '';
	@Input() reviewsFilters = false;
	@Input() trigger = false;
	closeOnSelection = input<boolean>(true);

	selector = 'rectangular';
	selected = false;
	contained = true;
	isBottom = true;
	showDropdown = false;
	checkedLabelList: string[] = [];
	currentSelected: DropdownSelect;
	selectBV$ = this.store.select(selectBVPixel);
	bvPixelResponse: BVPixelResponse;

	@Output() handleCheckedLabelList = new EventEmitter<string[]>();
	@Output() handleIndividualCheckedList = new EventEmitter<DropdownSelect>();
	@Output() handleUpdatedList = new EventEmitter<DropdownSelect[]>();
	@Output() toggleEvent = new EventEmitter<SelectedFilter>();
	@Output() dropdownEvent = new EventEmitter<boolean>();
	@Output() wasDefault = new EventEmitter<boolean>();

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconChevronDown,
			homefieldIconChevronUp
		]);
	}

	ngOnChanges(): void {
		if (!this.trigger || !this.dropdownList.length) {
			return;
		}

		if (this.isMultipleChoice) {
			this.checkedLabelList = [];
			this.dropdownList.forEach((option) => {
				this.storeMultipleChoice(option.selected, option.label);
			});
		} else {
			const selectedOption = this.dropdownList.find(({ selected }) => selected);
			if (selectedOption) {
				this.storeSingleChoice(true, selectedOption.label);
			}
		}

		this.wasDefault.emit(this.trigger);
	}

	ngOnInit(): void {
		this.selectBV$.pipe(take(1)).subscribe((bv: BVPixelResponse) => {
			this.bvPixelResponse = bv;
		});
	}

	toggleSelect(): void {
		if (this.disabled) {
			return;
		}

		this.selected = !this.selected;
		this.showDropdown = this.selected;
		this.toggleEvent.emit({ name: this.filterName, value: this.selected });
		this.dropdownEvent.emit(this.showDropdown);
	}

	getSelectedValue(selected: boolean, { key, label, ...props }: DropdownSelect): void {
		if (this.isMultipleChoice) {
			this.storeMultipleChoice(selected, label);
		} else {
			this.storeSingleChoice(selected, label);
		}
		if (this.reviewsFilters) {
			this.bvPixelEvent(
				this.isMultipleChoice ? BVEventObjectName.FILTER : BVEventObjectName.SORT,
				label
			);
		}
		if (this.closeOnSelection()) {
			this.hideMenu();
		}
		this.currentSelected = { ...props, selected, key, label };

		const updatedList = this.updatingDropdownList(selected, key);

		this.emitUpdatedData(updatedList);
	}

	hideMenu(): void {
		if (this.disabled) {
			return;
		}

		this.selected = false;
		this.showDropdown = false;
		this.dropdownEvent.emit(this.showDropdown);
	}

	handleElementPosition(isVisble: boolean): void {
		this.isBottom = isVisble;
	}

	handleClickEvent(isClickedOutside: boolean): void {
		if (isClickedOutside) {
			this.showDropdown = false;
			this.selected = false;
		}
	}

	protected trackByKey(_: number, dropdownData: DropdownSelect): string {
		return dropdownData.key;
	}

	private storeSingleChoice(selected: boolean, label: string): void {
		const updatedSelection = selected ? [label] : [];
		this.checkedLabelList = updatedSelection;
	}

	private storeMultipleChoice(selected: boolean, label: string): void {
		if (selected) {
			this.checkedLabelList.push(label);
		} else {
			const index = this.checkedLabelList.indexOf(label);
			if (index !== -1) {
				this.checkedLabelList.splice(index, 1);
			}
		}
	}

	private updatingDropdownList(selected: boolean, key: string): DropdownSelect[] {
		const updatedList = [...this.dropdownList];

		return updatedList.map((dropdownData: DropdownSelect) => {
			const defaultSelectValue = this.isMultipleChoice ? dropdownData.selected : false;
			const selectionUpdated = key === dropdownData.key ? selected : defaultSelectValue;

			return { ...dropdownData, selected: selectionUpdated };
		});
	}

	private emitUpdatedData(updatedList: DropdownSelect[]): void {
		// share checked name list
		this.handleCheckedLabelList.emit(this.checkedLabelList);

		// share individual selected item
		this.handleIndividualCheckedList.emit(this.currentSelected);

		// share initial list updated with selected values
		this.handleUpdatedList.emit(updatedList);
	}

	lostFocus(e: KeyboardEvent, index: number): void {
		if (e.key === 'Tab' && index === this.dropdownList.length - 1) {
			this.hideMenu();
		}
	}

	private bvPixelEvent(name: string, detail1?: string, detail2?: string): void {
		const bvObject: BVEventObject = {
			type: BVEventObjectType.USED,
			name: name,
			brand: this.bvPixelResponse.brand,
			productId: this.bvPixelResponse.productId,
			bvProduct: BVProduct.RATINGSANDREVIEWS,
			categoryId: this.bvPixelResponse.category,
			detail1: detail1 || '',
			detail2: detail2 || ''
		};
		BV.pixel.trackEvent(BVEventType.FEATURE, bvObject);
	}
}
